import {
  DropdownSelect,
  DropdownSelectItemType,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectItem,
  Text,
  TooltipProps,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import type {
  ConversationBulkActionType,
  ConversationsIdsBulkValidationType,
} from 'lib-shared-types-api';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { conversationsApi } from 'apis/conversations';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { ConversationsBulkModalCancelCsatAction } from './components/actions/cancelCsat/conversationsBulkModalCancelCsatAction';
import { ConversationsBulkModalCloseAction } from './components/actions/close/conversationsBulkModalCloseAction';
import { ConversationsBulkModalInternalNoteAction } from './components/actions/internalNote/conversationsBulkModalInternalNoteAction';
import { ConversationsBulkModalSendReplyAction } from './components/actions/sendReply/conversationsBulkModalSendReplyAction';
import { ConversationsBulkModalTagAction } from './components/actions/tag/conversationsBulkModalTagAction';
import { ConversationsBulkModalTransferToQueueAction } from './components/actions/transferToQueue/conversationsBulkModalTransferToQueueAction';
import { conversationsBulkModalActions } from './conversationsBulkModal.actions';
import styles from './conversationsBulkModal.module.scss';
import {
  BulkModalAction,
  BulkModalActionType,
} from './types/conversationsBulkModal.types';
import { conversationsBulkModalActionLabel } from './utils/conversationsBulkModalActionLabel';
import { getFailedValidationErrorMessage } from './utils/getFailedValidationErrorMessage';

const bulkActionSegmentNameMap: Record<ConversationBulkActionType, string> = {
  add_internal_note: 'addInternalNote',
  add_tag: 'addTag',
  cancel_csat: 'cancelCSAT',
  claim: ' claim',
  close: 'close',
  remove_tag: 'removeTag',
  send_reply: 'sendReply',
  transfer_to_queue: 'transferToQueue',
  link: 'link',
};

const bulkActionValidationNameMap: Record<
  ConversationsIdsBulkValidationType,
  ConversationBulkActionType
> = {
  SEND_REPLY: 'send_reply',
};

/** Represents the modal for performing bulk actions on conversations. */
export const ConversationsBulkModal = createAppModal(
  { type: 'app:conversationsBulk', featureFlag: 'has-bulk-operations' },
  ({ isOpen, modal, meta, hide }): JSX.Element => {
    const dispatch = useAppDispatch();
    const conversationIds = useMemo(
      () => modal?.arg.conversationIds || [],
      [modal?.arg.conversationIds],
    );

    const hasReplyAccess = useHasAccess('conversation.reply');
    const hasTagAccess = useHasAccess('conversation.tag');
    const hasSatisfactionAccess = useHasAccess('conversation.satisfaction');

    const [
      readConversationsValidationBulk,
      { data: readConversationsValidationBulkData, isFetching, isSuccess },
    ] = conversationsApi.useLazyReadConversationsValidationBulkQuery();

    // Handles getting validation information.
    useEffect(() => {
      if (isOpen) {
        readConversationsValidationBulk({ csids: conversationIds });
      }
    }, [conversationIds, isOpen, readConversationsValidationBulk]);

    // Keeps track of selected actions.
    const [selectedActions, setSelectedActions] = useState<
      readonly BulkModalAction[]
    >([]);

    // Keeps track of selected action types.
    const [selectedActionTypes, setSelectedActionTypes] = useState<
      readonly BulkModalActionType[]
    >([]);

    const canPerformBulkUpdate = useMemo(
      () =>
        selectedActions.length > 0 &&
        selectedActions.every((selectedAction) => selectedAction.isValid),
      [selectedActions],
    );

    /** Hides and resets the modal to initial state. */
    const handleResetAndHide = () => {
      setSelectedActionTypes([]);
      setSelectedActions([]);
      hide();
    };

    /** Handles confirming the modal and performing the bulk actions. */
    const handleConfirm = () => {
      if (meta) {
        ampli.bulkActionsUpdateClicked({
          selectedActions: selectedActions.map(
            ({ type }) => bulkActionSegmentNameMap[type],
          ),
          selectedActionsCount: selectedActions.length,
          conversationsCount: conversationIds.length,
          location: modal.arg.location,
          modalVersion: '2',
        });

        dispatch(
          conversationsBulkModalActions.initBulkUpdate({
            actions: selectedActions,
          }),
        );

        meta.resolve({ isConfirmed: true, isCancelled: false });

        handleResetAndHide();
      }
    };

    /** Handles cancelling the modal. */
    const handleCancel = () => {
      if (meta) {
        handleResetAndHide();

        meta.resolve({ isConfirmed: false, isCancelled: true });
      }
    };

    /** Handles selection of an action. */
    const handleActionSelect = useCallback(
      ({ id: actionType }: DropdownSelectItemType) => {
        const type = actionType as BulkModalActionType;
        if (!selectedActionTypes.includes(type)) {
          setSelectedActionTypes((previousSelectedActionTypes) => [
            ...previousSelectedActionTypes,
            type,
          ]);
        }
      },
      [selectedActionTypes],
    );

    /** The rendered dropdown selectable actions. */
    const dropdownSelectActions = useMemo(
      () =>
        (
          Object.keys(
            conversationsBulkModalActionLabel,
          ) as readonly BulkModalActionType[]
        )
          .filter((bulkAction: BulkModalActionType) => {
            if (selectedActionTypes.includes(bulkAction)) {
              return false;
            }

            switch (bulkAction) {
              case 'link': // Link action is not available for selection.
                return false;
              case 'close':
              case 'send_reply':
              case 'transfer_to_queue':
                return hasReplyAccess;
              case 'add_tag':
              case 'remove_tag':
                return hasTagAccess;
              case 'cancel_csat':
                return hasSatisfactionAccess;
              default:
                return true;
            }
          })
          .map<DropdownSelectItemType>((unselectedAction) => ({
            id: unselectedAction,
            name: conversationsBulkModalActionLabel[unselectedAction],
          })),
      [
        selectedActionTypes,
        hasReplyAccess,
        hasTagAccess,
        hasSatisfactionAccess,
      ],
    );

    /** The maximum allowed reply text length, or `null` when there is no limit. */
    const maxTextLength =
      readConversationsValidationBulkData?.data.max_text_length ?? null;

    /** The ids of the disabled actions. */
    const disabledActionIds = useMemo(
      () =>
        (
          readConversationsValidationBulkData?.data.validations_failed ?? []
        ).map(
          ({ validation_type }) => bulkActionValidationNameMap[validation_type],
        ),
      [readConversationsValidationBulkData],
    );

    /** The dropdown tooltip items. */
    const dropdownSelectTooltipItems = useMemo<
      Partial<Record<ConversationBulkActionType, TooltipProps>>
    >(() => {
      if (!readConversationsValidationBulkData?.data.validations_failed) {
        return {};
      }

      const validationErrorMessages =
        readConversationsValidationBulkData.data.validations_failed.reduce<
          Partial<Record<ConversationBulkActionType, readonly string[]>>
        >((failedValidationMessages, { csids, error, validation_type }) => {
          const bulkActionType = bulkActionValidationNameMap[validation_type];

          return {
            ...failedValidationMessages,
            [bulkActionType]: [
              failedValidationMessages[bulkActionType],
              getFailedValidationErrorMessage({ csids, error }),
            ].filter(Boolean),
          };
        }, {});

      return Object.keys(validationErrorMessages).reduce<
        Partial<Record<ConversationBulkActionType, TooltipProps>>
      >(
        (tooltipItems, bulkActionType) => ({
          ...tooltipItems,
          [bulkActionType]: {
            content: (
              <>
                <strong>
                  {conversationsBulkModalActionLabel[bulkActionType]} action is
                  disabled
                </strong>

                <ul className={styles.tooltipList}>
                  {validationErrorMessages[bulkActionType].map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </>
            ),
          },
        }),
        {},
      );
    }, [readConversationsValidationBulkData]);

    /**
     * Handles the change of an action.
     *
     * @param action The bulk action object.
     */
    const handleActionChange = useCallback(
      (action: BulkModalAction) =>
        setSelectedActions((previousSelectedActions) =>
          previousSelectedActions.find(
            (selectedAction) => selectedAction.type === action.type,
          )
            ? previousSelectedActions.map((previousSelectedAction) =>
                previousSelectedAction.type === action.type
                  ? action
                  : previousSelectedAction,
              )
            : [...previousSelectedActions, action],
        ),
      [],
    );

    /**
     * Removes action from currently selected action types.
     *
     * @param type The action type to remove.
     */
    const handleActionRemove = useCallback((type: BulkModalActionType) => {
      setSelectedActionTypes((previousSelectedActionTypes) =>
        previousSelectedActionTypes.filter(
          (previousSelectedActionType) => previousSelectedActionType !== type,
        ),
      );

      setSelectedActions((previousSelectedActions) =>
        previousSelectedActions.filter(
          (previousSelectedAction) => previousSelectedAction.type !== type,
        ),
      );

      // TODO: [TEAMS-8097] Add tracking events
    }, []);

    /** The map of actions elements. */
    const actionElements: Record<BulkModalActionType, ReactNode> = useMemo(
      () => ({
        add_internal_note: (
          <ConversationsBulkModalInternalNoteAction
            key="add_internal_note"
            conversationIds={conversationIds}
            onRemove={handleActionRemove}
            onChange={handleActionChange}
          />
        ),
        add_tag: (
          <ConversationsBulkModalTagAction
            key="add_tag"
            type="add_tag"
            conversationIds={conversationIds}
            onRemove={handleActionRemove}
            onChange={handleActionChange}
          />
        ),
        cancel_csat: (
          <ConversationsBulkModalCancelCsatAction
            key="cancel_csat"
            conversationIds={conversationIds}
            onRemove={handleActionRemove}
            onChange={handleActionChange}
          />
        ),
        close: (
          <ConversationsBulkModalCloseAction
            key="close"
            conversationIds={conversationIds}
            onRemove={handleActionRemove}
            onChange={handleActionChange}
          />
        ),
        remove_tag: (
          <ConversationsBulkModalTagAction
            key="remove_tag"
            type="remove_tag"
            conversationIds={conversationIds}
            onRemove={handleActionRemove}
            onChange={handleActionChange}
          />
        ),
        send_reply: (
          <ConversationsBulkModalSendReplyAction
            key="send_reply"
            conversationIds={conversationIds}
            maxTextLength={maxTextLength}
            onRemove={handleActionRemove}
            onChange={handleActionChange}
          />
        ),
        transfer_to_queue: (
          <ConversationsBulkModalTransferToQueueAction
            key="transfer_to_queue"
            conversationIds={conversationIds}
            onRemove={handleActionRemove}
            onChange={handleActionChange}
          />
        ),
        link: null,
      }),
      [conversationIds, handleActionChange, handleActionRemove, maxTextLength],
    );

    /** The rendered selected action elements. */
    const selectedActionElements = useMemo(
      () =>
        selectedActionTypes.map(
          (selectedAction) => actionElements[selectedAction],
        ),
      [actionElements, selectedActionTypes],
    );

    return (
      <Modal isOpen={isOpen} onClose={handleCancel} className={styles.root}>
        <ModalHead
          title="Update conversations in bulk"
          onClose={handleCancel}
        />

        <ModalBody>
          <Text.Paragraph withMarginBottom>
            Choose which actions you would like to apply to the conversations
            that you have selected. You can choose one action or add multiple
            actions to be applied after each other.
          </Text.Paragraph>

          {selectedActionElements}
        </ModalBody>

        <ModalFoot
          cancelName="Cancel"
          cancelOnClick={() => handleCancel()}
          confirmName={`Update (${conversationIds.length})`}
          confirmOnClick={() => handleConfirm()}
          confirmProps={{ variant: 'primary', disabled: !canPerformBulkUpdate }}
        >
          <DropdownSelect
            containerProps={{ className: styles.actionSelectDropdownContainer }}
            className={styles.actionSelect}
            items={dropdownSelectActions}
            itemComponent={SelectItem}
            noArrow={false}
            buttonProps={{
              disabled:
                !dropdownSelectActions.length || isFetching || !isSuccess,
            }}
            disabledItems={disabledActionIds}
            tooltipItems={dropdownSelectTooltipItems}
            onSelect={handleActionSelect}
            itemsDisplayCount={dropdownSelectActions.length}
          >
            Add action
          </DropdownSelect>
        </ModalFoot>
      </Modal>
    );
  },
);
